import React, { useState, useMemo, useRef, useEffect } from 'react';
import { CSActivity, Contact, User, SalesRep } from '../types';
import { 
    Plus, Search, Filter, Calendar, Coins, MessageSquare, 
    User as UserIcon, Building2, Wrench, CheckCircle2, 
    AlertCircle, BarChart3, X, ChevronDown, Trash2, Send, Edit3 
} from 'lucide-react';
// 🚨 [데이터 서비스] 전체 히스토리를 긁어오기 위해 임포트
import { crmDataService } from '../services/crmDataService'; 
// 🚨 [추가] 따로 분리한 모달 부품 가져오기
import CSActivityModal from './CSActivityModal'; 
import PermissionGate from './PermissionGate'; // 🚨 [추가] 자물쇠 마법상자 불러오기

interface CSActivityManagementProps {
    activities: CSActivity[];
    contacts: Contact[];
    user: User | null; // 🚨 안전장치: user가 없을 수도 있음을 명시
    salesReps: SalesRep[];
    onAddActivity: (activity: CSActivity) => void;
    // ✅ [기능 추가] 삭제 및 매출예정표 전달 함수 프롭스
    onDeleteActivity: (id: string) => void;
    onConvertToDeal: (activity: CSActivity) => void;
    globalRefreshKey?: number;
    // 🚨 [추가] 알림창에서 클릭한 항목을 받을 수 있는 Props
    targetActivity?: any;
    onClearTarget?: () => void;
}

// 🚨 한국 시간(KST) 기준 오늘 날짜 구하기 헬퍼 함수
const getTodayKST = () => {
    const now = new Date();
    const utcNow = now.getTime() + (now.getTimezoneOffset() * 60000);
    const kstTime = new Date(utcNow + (9 * 3600000));
    return kstTime.toISOString().split('T')[0];
};

const CSActivityManagement: React.FC<CSActivityManagementProps> = ({ 
    activities, contacts, user, salesReps, onAddActivity, onDeleteActivity, onConvertToDeal, globalRefreshKey = 0,
    targetActivity, onClearTarget // 👈 바통 받기
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [filterSalesRep, setFilterSalesRep] = useState<string>('all');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    
    // 🚨 [구조 변경] 기존의 복잡한 입력 폼 상태를 삭제하고
    // 모달을 열고 닫는 스위치 역할과 수정 대상 데이터 상태만 관리합니다.
    const [isAdding, setIsAdding] = useState(false);
    const [editingActivity, setEditingActivity] = useState<CSActivity | null>(null);

    // 🚨 [신규 추가] 대시보드의 '전체 피드'를 가져와 담을 바구니
    const [csHistoryFeed, setCsHistoryFeed] = useState<any[]>([]);

    // 💡 [데이터 로드] 컴포넌트가 열릴 때 대시보드 피드를 가져옵니다.
    useEffect(() => {
        const loadFeed = async () => {
            try {
                // days=0을 주어 기간 제한 없이 전체 피드를 가져옵니다.
                const data = await crmDataService.getDashboardActivityFeed(0);
                setCsHistoryFeed(data || []);
            } catch (error) {
                console.error("대시보드 피드 로드 실패:", error);
            }
        };
        loadFeed();
    }, [globalRefreshKey]);

    // 🚨 [추가] 알림창에서 특정 항목(targetActivity)을 받아오면 모달을 자동으로 엽니다!
    useEffect(() => {
        if (targetActivity) {
            handleEdit(targetActivity);
            if (onClearTarget) onClearTarget(); // 팝업 띄웠으니 바통 리셋
        }
    }, [targetActivity, onClearTarget]);

    const handleEdit = (activity: CSActivity) => {
        setEditingActivity(activity);
        setIsAdding(true); // 분리된 모달을 띄웁니다.
    };

    // 🎯 [핵심 로직] 대시보드 피드에서 거래처명이 일치하고, 에디터에 [CS]가 포함된 내역을 카운트
    const getCompanyCSCount = (companyName: string) => {
        if (!companyName || csHistoryFeed.length === 0) return 0;
        
        const targetName = companyName.trim().toLowerCase();

        return csHistoryFeed.filter((item: any) => {
            const hCompany = (item.deal_id || "").trim().toLowerCase();
            const editor = (item.editor || "").toUpperCase();
            
            // 거래처 이름이 같고, 작성자에 'CS'가 들어간 것만!
            return hCompany === targetName && editor.includes('CS');
        }).length;
    };

    const filteredActivities = useMemo(() => {
        return activities.filter(activity => {
            const matchesSearch =
                activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                activity.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                activity.contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                activity.phone.includes(searchTerm) ||
                activity.email.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesFilter = filterType === 'all' || activity.type === filterType;
            const matchesStatus = filterStatus === 'all' || activity.status === filterStatus;
            const matchesSalesRep = filterSalesRep === 'all' || activity.salesRep === filterSalesRep;

            // Date Range Filter
            const matchesStartDate = !startDate || activity.date >= startDate;
            const matchesEndDate = !endDate || activity.date <= endDate;

            return matchesSearch && matchesFilter && matchesStatus && matchesSalesRep && matchesStartDate && matchesEndDate;
        }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [activities, searchTerm, filterType, filterStatus, filterSalesRep, startDate, endDate]);

    // 🚨 [수정 및 반영] CS 활동 기록이 있는 담당자만 필터 목록에 추출
    const activeSalesReps = useMemo(() => {
        const names = Array.from(new Set(activities.map(a => a.salesRep).filter(Boolean)));
        return names.sort(); // 가나다순 정렬
    }, [activities]);

    const stats = useMemo(() => {
        const totalCost = filteredActivities.reduce((sum, a) => sum + a.cost, 0);
        
        // 🚨 [산출] 현재 검색된 업체들이 보유한 'CS 피드' 총합 계산
        const totalCSActivities = filteredActivities.reduce((sum, a) => sum + getCompanyCSCount(a.company), 0);

        // Type Stats
        const typeStats = filteredActivities.reduce((acc, a) => {
            acc[a.type] = (acc[a.type] || 0) + 1;
            acc.total++;
            return acc;
        }, { AS: 0, 교육: 0, BS: 0, 지원: 0, 기타: 0, total: 0 } as Record<string, number>);

        // Status Stats (🚨 타입스크립트 에러 완벽 해결 반영됨)
        const statusStats = filteredActivities.reduce((acc, a) => {
            acc[a.status] = (acc[a.status] || 0) + 1;
            acc.total++;
            return acc;
        }, { 접수: 0, 진행: 0, 완료: 0, total: 0 } as Record<string, number>);

        return { totalCost, totalCSActivities, typeStats, statusStats };
    }, [filteredActivities, csHistoryFeed]);

    return (
        <div className="space-y-6">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Card 1: Combined Count & Visits */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                            <Wrench size={20} />
                        </div>
                        <span className="text-sm font-medium text-slate-500">접수 및 활동 현황</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-xs text-slate-400 font-bold uppercase mb-1">총 접수</p>
                            <p className="text-2xl font-black text-slate-900">{filteredActivities.length}<span className="text-xs font-normal text-slate-400 ml-1">건</span></p>
                        </div>
                        <div className="border-l border-slate-100 pl-4">
                            <p className="text-xs text-slate-400 font-bold uppercase mb-1">CS 누적</p>
                            <p className="text-2xl font-black text-indigo-600">{stats.totalCSActivities}<span className="text-xs font-normal text-slate-400 ml-1">건</span></p>
                        </div>
                    </div>
                </div>

                {/* Card 2: Total Cost */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
                            <Coins size={20} />
                        </div>
                        <span className="text-sm font-medium text-slate-500">총 소요 비용</span>
                    </div>
                    <p className="text-2xl font-black text-slate-900">{stats.totalCost.toLocaleString()} <span className="text-sm font-normal text-slate-400">원</span></p>
                    <p className="text-[10px] text-slate-400 mt-2 font-medium italic">* 필터링된 결과 기준</p>
                </div>

                {/* Card 3: Type Breakdown */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                            <BarChart3 size={20} />
                        </div>
                        <span className="text-sm font-medium text-slate-500">유형별 활동 현황</span>
                    </div>
                    <div className="space-y-2">
                        {['AS', '교육', 'BS', '지원', '기타'].map((type) => {
                            const count = stats.typeStats[type] || 0;
                            const percentage = stats.typeStats.total > 0 ? Math.round((count / stats.typeStats.total) * 100) : 0;
                            const colors: Record<string, string> = {
                                AS: 'bg-red-500', 교육: 'bg-blue-500', BS: 'bg-emerald-500',
                                지원: 'bg-amber-500', 기타: 'bg-slate-500'
                            };
                            return (
                                <div key={type} className="space-y-1">
                                    <div className="flex justify-between text-[10px] font-bold">
                                        <span className="text-slate-500">{type}</span>
                                        <span className="text-slate-900">{count}건 ({percentage}%)</span>
                                    </div>
                                    <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                                        <div className={`h-full ${colors[type]} transition-all duration-500`} style={{ width: `${percentage}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Card 4: Status Breakdown */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                            <AlertCircle size={20} />
                        </div>
                        <span className="text-sm font-medium text-slate-500">상태별 활동 현황</span>
                    </div>
                    <div className="space-y-2">
                        {[
                            { label: '접수', count: stats.statusStats.접수, color: 'bg-slate-400' },
                            { label: '진행', count: stats.statusStats.진행, color: 'bg-blue-500' },
                            { label: '완료', count: stats.statusStats.완료, color: 'bg-emerald-500' }
                        ].map((item) => {
                            const percentage = stats.statusStats.total > 0 ? Math.round((item.count / stats.statusStats.total) * 100) : 0;
                            return (
                                <div key={item.label} className="space-y-1">
                                    <div className="flex justify-between text-[10px] font-bold">
                                        <span className="text-slate-500">{item.label}</span>
                                        <span className="text-slate-900">{item.count}건 ({percentage}%)</span>
                                    </div>
                                    <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                                        <div className={`h-full ${item.color} transition-all duration-500`} style={{ width: `${percentage}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Actions & Filters */}
            <div className="flex flex-col xl:flex-row justify-between items-center gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
                    <div className="relative flex-1 min-w-[240px] md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                            type="text"
                            placeholder="거래처, 담당자, 연락처 검색..."
                            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all text-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5">
                        <Calendar size={16} className="text-blue-500" />
                        <input type="date" className="bg-transparent border-none text-xs focus:ring-0 outline-none w-28" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        <span className="text-slate-400 text-xs">~</span>
                        <input type="date" className="bg-transparent border-none text-xs focus:ring-0 outline-none w-28" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        {(startDate || endDate) && (
                            <button onClick={() => { setStartDate(''); setEndDate(''); }} className="p-1 hover:bg-slate-200 rounded-full text-slate-400 transition-colors" title="날짜 필터 초기화">
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    <select className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm" value={filterSalesRep} onChange={(e) => setFilterSalesRep(e.target.value)}>
                        <option value="all">모든 담당자</option>
                        {activeSalesReps.map(name => (<option key={name} value={name}>{name}</option>))}
                    </select>
                    <select className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="all">모든 상태</option>
                        <option value="접수">접수</option><option value="진행">진행</option><option value="완료">완료</option>
                    </select>
                    <select className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                        <option value="all">모든 유형</option>
                        <option value="AS">AS (수리)</option><option value="교육">교육</option><option value="BS">BS (사전점검)</option><option value="지원">지원</option><option value="기타">기타</option>
                    </select>
                </div>
                {/* 🛡️ 자물쇠 채우기: 새 CS 활동 기록 버튼  미사용으로인해 주석 코드는 살아있어서 주석 제거하면 바로 살아남 */}
                {/* <PermissionGate user={user} action="cs_edit">
                    <button
                        onClick={() => { setEditingActivity(null); setIsAdding(true); }}
                        className="w-full xl:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 whitespace-nowrap"
                    >
                        <Plus size={20} /> 새 CS 활동 기록
                    </button>
                </PermissionGate> */}
            </div>

            {/* Activity List */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-bottom border-slate-200">
                                <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">날짜</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">상태</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">유형</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">거래처</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">담당자</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">활동 내용</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">소요 비용</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider text-center">CS 피드</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">영업 담당자</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">영업 전달사항</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider text-center">관리</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredActivities.length > 0 ? (
                                filteredActivities.map((activity) => (
                                    <tr key={activity.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4"><span className="text-sm font-bold text-slate-900">{activity.date}</span></td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black w-fit ${
                                                activity.status === '접수' ? 'bg-slate-100 text-slate-600' :
                                                activity.status === '진행' ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'
                                            }`}>{activity.status}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black w-fit ${
                                                activity.type === 'AS' ? 'bg-red-100 text-red-700' :
                                                activity.type === '교육' ? 'bg-blue-100 text-blue-700' :
                                                activity.type === 'BS' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'
                                            }`}>{activity.type}</span>
                                        </td>
                                        <td className="px-6 py-4"><span className="text-sm font-bold text-slate-900">{activity.company}</span></td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-slate-900">{activity.contactName}</span>
                                                <span className="text-[10px] text-slate-500">{activity.phone}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4"><p className="text-sm text-slate-600 line-clamp-2 max-w-xs">{activity.description}</p></td>
                                        <td className="px-6 py-4"><span className="text-sm font-bold text-slate-900">{activity.cost.toLocaleString()}원</span></td>
                                        
                                        <td className="px-6 py-4 text-center">
                                            <span className="text-sm font-black text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-100">
                                                {getCompanyCSCount(activity.company)}건
                                            </span>
                                        </td>

                                        <td className="px-6 py-4">
                                            <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-lg border border-blue-100">{activity.salesRep || '미지정'}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-start gap-2 max-w-xs">
                                                <MessageSquare size={14} className="text-blue-500 mt-1 shrink-0" />
                                                <p className="text-xs italic text-slate-500">{activity.salesNote || '전달사항 없음'}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <PermissionGate user={user} action="cs_edit">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button onClick={() => { if (window.confirm(`${activity.company} 건을 영업 기회로 넘길까요?`)) onConvertToDeal(activity); }}
                                                        className="flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-md border border-amber-100 shadow-sm"
                                                    >
                                                        <Send size={12} className="fill-amber-700/20" /> <span className="text-[10px] font-black">매출 등록</span>
                                                    </button>
                                                    <button onClick={() => handleEdit(activity)} className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-md border border-blue-100 shadow-sm">
                                                        <Edit3 size={12} /> <span className="text-[10px] font-black">수정</span>
                                                    </button>
                                                    <button 
                                                        onClick={async () => { 
                                                            if (window.confirm("정말 이 CS 기록을 삭제하시겠습니까?")) {
                                                                try {
                                                                    await onDeleteActivity(activity.id);
                                                                    alert("삭제되었습니다.");
                                                                } catch (err) {
                                                                    alert("삭제 처리 중 오류가 발생했습니다.");
                                                                }
                                                            } 
                                                        }}
                                                        className="flex items-center gap-1 px-2 py-1 bg-red-50 text-red-600 hover:bg-red-100 rounded-md border border-red-100 shadow-sm"
                                                    >
                                                        <Trash2 size={12} /> <span className="text-[10px] font-black">삭제</span>
                                                    </button>
                                                </div>
                                            </PermissionGate>
                                            {!user?.permissions?.edit_cs && user?.role !== 'admin' && (
                                                <span className="text-slate-300 text-[10px] font-black">접근 권한 없음</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={11} className="px-6 py-12 text-center text-slate-400">기록된 CS 활동이 없습니다.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <CSActivityModal 
                isOpen={isAdding} 
                onClose={() => { setIsAdding(false); setEditingActivity(null); }} 
                clientName={editingActivity?.company} 
                initialData={editingActivity || undefined}
                onSuccess={() => {
                    setIsAdding(false); 
                    setEditingActivity(null);
                    alert("성공적으로 처리되었습니다.");
                }} 
            />
        </div>
    );
};

export default CSActivityManagement;